import React, { useRef, useState } from "react";
import { FileText, Upload, Trash2, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const KINDS = [
  { kind: "cover", field: "cover_pdf", title: "Cover page", hint: "Goes in front of the quotation" },
  { kind: "closing", field: "closing_pdf", title: "Closing page", hint: "Terms, company profile or references at the back" },
];

const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const Slot = ({ meta, title, hint, busy, onPick, onRemove, testid }) => (
  <div data-testid={testid} className="border border-border rounded-xl p-4 flex items-start gap-4">
    <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${meta ? "bg-brand-soft text-brand" : "bg-secondary text-muted-foreground"}`}>
      <FileText className="w-5 h-5" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-bold text-slate-900 dark:text-slate-100">{title}</div>
      {meta ? (
        <div className="text-xs text-muted-foreground mt-0.5 truncate" title={meta.filename}>
          {meta.filename} · {meta.pages} page{meta.pages === 1 ? "" : "s"} · {kb(meta.size || 0)}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>
      )}
      <div className="flex items-center gap-2 mt-3">
        <Button variant="outline" size="sm" data-testid={`${testid}-upload`} onClick={onPick} disabled={busy} className="h-8 gap-1.5 font-semibold">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {meta ? "Replace" : "Upload PDF"}
        </Button>
        {meta && (
          <Button variant="ghost" size="sm" data-testid={`${testid}-remove`} onClick={onRemove} className="h-8 gap-1.5 text-muted-foreground">
            <Trash2 className="w-3.5 h-3.5" /> Remove
          </Button>
        )}
      </div>
    </div>
  </div>
);

/** Cover / closing PDF pages wrapped around a generated quotation.
 *  scope="profile" saves the default on the vendor; scope={rfqId} overrides one quote. */
export const QuotePdfPages = ({ scope, value = {}, onChange, testid = "quote-pdf-pages" }) => {
  const [busy, setBusy] = useState("");
  const refs = { cover: useRef(), closing: useRef() };
  const path = scope === "profile"
    ? "/profile/quote-pdf"
    : (typeof scope === "string" && scope.startsWith("template:"))
      ? `/quote-templates/${scope.slice(9)}/quote-pdf`
      : `/rfqs/${scope}/quote-pdf`;

  const upload = (kind) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(kind);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    try {
      const { data } = await api.post(path, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind === "cover" ? "Cover" : "Closing"} page attached — ${data[kind].pages} page${data[kind].pages === 1 ? "" : "s"}`);
      onChange?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not attach that PDF");
    } finally {
      setBusy("");
      if (refs[kind].current) refs[kind].current.value = "";
    }
  };

  const remove = (kind) => async () => {
    try {
      await api.delete(`${path}?kind=${kind}`);
      toast.success(`${kind === "cover" ? "Cover" : "Closing"} page removed`);
      onChange?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid={testid} className="grid sm:grid-cols-2 gap-3">
      {KINDS.map(({ kind, field, title, hint }) => (
        <React.Fragment key={kind}>
          <input ref={refs[kind]} type="file" accept="application/pdf" hidden
                 onChange={upload(kind)} data-testid={`${testid}-${kind}-input`} />
          <Slot
            meta={value?.[field]}
            title={title}
            hint={hint}
            busy={busy === kind}
            onPick={() => refs[kind].current?.click()}
            onRemove={remove(kind)}
            testid={`${testid}-${kind}`}
          />
        </React.Fragment>
      ))}
    </div>
  );
};
